import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { procurementApi, STAGE_LABELS, STATUS_LABELS, CATEGORY_LABELS, PROCUREMENT_STAGES, formatMoney } from '../../api/procurement';
import { projectsApi } from '../../api/projects';
import { ApiError } from '../../api/client';
import { usePermission } from '../../auth/AuthContext';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';

interface CreateForm { title: string; category: string; projectId: string; estimatedValue: string; requiredByDate: string; description: string; }

export function ProcurementListPage() {
  const canCreate = usePermission('procurement.manage');
  const navigate = useNavigate();
  const { showError } = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const { register, handleSubmit, reset } = useForm<CreateForm>({ defaultValues: { category: 'goods' } });

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 350); return () => clearTimeout(t); }, [search]);

  const listQuery = useQuery({
    queryKey: ['procurements', debouncedSearch, stageFilter, statusFilter],
    queryFn: () => procurementApi.list({ search: debouncedSearch || undefined, stage: stageFilter || undefined, status: statusFilter || undefined }),
  });
  const projectsQuery = useQuery({ queryKey: ['projects'], queryFn: () => projectsApi.list(), enabled: showCreate });

  const createMutation = useMutation({
    mutationFn: (v: CreateForm) => procurementApi.create({
      title: v.title, category: v.category, description: v.description || null,
      projectId: v.projectId ? Number(v.projectId) : null,
      estimatedValue: v.estimatedValue ? Number(v.estimatedValue) : null,
      requiredByDate: v.requiredByDate || null,
    }),
    onSuccess: (r) => { reset(); setShowCreate(false); navigate(`/procurement/${r.data.id}`); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create procurement.'),
  });

  return (
    <div className="page">
      <PageHeader title="Procurement" description="Procurement files from request through sourcing, evaluation, award, delivery and closeout." />
      <div className="panel">
        <SectionHeader title="Procurements" actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Search title or reference…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 210 }} />
            <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
              <option value="">All Stages</option>
              {PROCUREMENT_STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {canCreate && <button onClick={() => setShowCreate(true)}>+ New Procurement</button>}
          </div>
        } />
        <div className="panel-body">
          {listQuery.isError && <div className="error-state">Failed to load procurements.</div>}
          <DataTable
            rows={listQuery.data?.data ?? []}
            rowKey={(p) => p.id}
            emptyMessage={listQuery.isLoading ? 'Loading…' : 'No procurements yet.'}
            columns={[
              { header: 'Reference', render: (p) => <Link to={`/procurement/${p.id}`}>{p.reference}</Link> },
              { header: 'Title', render: (p) => p.title },
              { header: 'Project', render: (p) => p.project_name ?? '-' },
              { header: 'Category', render: (p) => CATEGORY_LABELS[p.category] ?? p.category },
              { header: 'Est. Value', render: (p) => formatMoney(p.estimated_value, p.currency_code) },
              { header: 'Stage', render: (p) => STAGE_LABELS[p.stage] ?? p.stage },
              { header: 'Status', render: (p) => <StatusBadge status={p.status} label={STATUS_LABELS[p.status]} /> },
            ]}
          />
        </div>
      </div>

      {showCreate && (
        <Modal title="New Procurement Request" onClose={() => setShowCreate(false)}>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <label>Title<input {...register('title', { required: true })} placeholder="e.g. Supply of Hygiene Kits" /></label>
            <div className="form-row">
              <label>Category
                <select {...register('category')}>{Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
              </label>
              <label>Project (optional)
                <select {...register('projectId')}>
                  <option value="">Not linked to a project</option>
                  {projectsQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                </select>
              </label>
            </div>
            <div className="form-row">
              <label>Estimated Value (NGN)<input type="number" min="0" step="0.01" {...register('estimatedValue')} /></label>
              <label>Required By<input type="date" {...register('requiredByDate')} /></label>
            </div>
            <label>Description<textarea rows={3} {...register('description')} /></label>
            <p className="muted" style={{ fontSize: 12 }}>The request is created as a draft. Add line items on the Requisition tab, then submit.</p>
            <div className="form-actions"><button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create Draft'}</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
